import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, apiClient } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Users, 
  Article, 
  ChatCircle,
  GraduationCap,
  Chalkboard,
  Trash,
  ShieldCheck,
  CaretDown,
  LinkSimple,
  Gift,
  Coins,
  Trophy,
  Key,
  CheckCircle,
  XCircle,
  Clock,
  DownloadSimple
} from "@phosphor-icons/react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const INVITE_EXPIRY_OPTIONS = [
  { value: 2, label: "48 hours" },
  { value: 7, label: "7 days" },
  { value: 14, label: "14 days" },
  { value: 30, label: "1 month" },
  { value: 60, label: "2 months" },
  { value: 90, label: "3 months" },
  { value: 120, label: "4 months" },
  { value: 180, label: "6 months" },
];

export default function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [referralStats, setReferralStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [invites, setInvites] = useState([]);
  const [inviteDays, setInviteDays] = useState(2);
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [selectedPointsUserId, setSelectedPointsUserId] = useState("");
  const [pointsTarget, setPointsTarget] = useState("bonus");
  const [pointsOperation, setPointsOperation] = useState("add");
  const [pointsValue, setPointsValue] = useState(0);
  const [pointsReason, setPointsReason] = useState("");
  const [adjustingPoints, setAdjustingPoints] = useState(false);
  const [activeTab, setActiveTab] = useState("stats");
  const [loading, setLoading] = useState(true);
  const [adminLeaderboard, setAdminLeaderboard] = useState([]);

  const handleExportLeaderboard = async () => {
    try {
      const response = await apiClient.get('/admin/leaderboard/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'diit_leaderboard.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to export leaderboard");
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, usersRes, postsRes, referralStatsRes, withdrawalsRes, leaderboardRes] = await Promise.all([
        apiClient.get("/admin/stats"),
        apiClient.get("/admin/users"),
        apiClient.get("/admin/posts"),
        apiClient.get("/admin/referral-stats"),
        apiClient.get("/admin/withdrawals"),
        apiClient.get("/leaderboard/public?page_size=1000")
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setPosts(postsRes.data);
      setReferralStats(referralStatsRes.data);
      setWithdrawals(withdrawalsRes.data);
      setAdminLeaderboard(leaderboardRes.data.entries || []);

      if (["admin", "super_admin"].includes(user?.role)) {
        const invitesRes = await apiClient.get("/invites");
        setInvites(invitesRes.data || []);
      }
    } catch (error) {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  }, [user?.role]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRoleChange = async (userId, newRole) => {
    try {
      await apiClient.put(`/admin/users/${userId}/role?role=${newRole}`);
      setUsers(users.map(u => u.id === userId ? { ...u, role: newRole } : u));
      toast.success("Role updated");
    } catch (error) {
      toast.error("Failed to update role");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm("Delete this user and all their content?")) return;
    
    try {
      await apiClient.delete(`/admin/users/${userId}`);
      setUsers(users.filter(u => u.id !== userId));
      toast.success("User deleted");
    } catch (error) {
      toast.error("Failed to delete user");
    }
  };

  const handleDeletePost = async (postId) => {
    if (!confirm("Delete this post?")) return;
    
    try {
      await apiClient.delete(`/posts/${postId}`);
      setPosts(posts.filter(p => p.id !== postId));
      toast.success("Post deleted");
    } catch (error) {
      toast.error("Failed to delete post");
    }
  };

  const handleFeaturePost = async (post, featured) => {
    try {
      await apiClient.post(`/admin/posts/${post.id}/feature`, { featured, priority: featured ? 10 : 0 });
      setPosts(posts.map(p => p.id === post.id ? { ...p, is_featured: featured } : p));
      toast.success(featured ? "Post featured" : "Post unfeatured");
    } catch (error) {
      toast.error("Failed to update feature status");
    }
  };

  const handlePinPost = async (post, pinned) => {
    try {
      await apiClient.post(`/admin/posts/${post.id}/pin`, { pinned });
      setPosts(posts.map(p => p.id === post.id ? { ...p, is_pinned: pinned } : p));
      toast.success(pinned ? "Post pinned" : "Post unpinned");
    } catch (error) {
      toast.error("Failed to pin post");
    }
  };

  const handleHidePost = async (post, hidden) => {
    try {
      await apiClient.post(`/admin/posts/${post.id}/hide`, { hidden });
      setPosts(posts.map(p => p.id === post.id ? { ...p, status: hidden ? "hidden" : "published" } : p));
      toast.success(hidden ? "Post hidden" : "Post unhidden");
    } catch (error) {
      toast.error("Failed to hide post");
    }
  };

  const handleBanUser = async (userId, fullName) => {
    const reason = prompt(`Ban reason for ${fullName}:`);
    if (!reason || reason.trim().length < 3) return;
    const daysInput = prompt("Ban duration in days (leave empty for permanent):", "7");
    if (daysInput === null) return;
    const permanent = daysInput.trim() === "";
    const duration_days = permanent ? null : Math.max(1, parseInt(daysInput, 10) || 7);

    try {
      await apiClient.post(`/admin/users/${userId}/ban`, { reason: reason.trim(), permanent, duration_days });
      setUsers(users.map(u => u.id === userId ? { ...u, banned_permanently: permanent, banned_until: permanent ? null : "set" } : u));
      toast.success(permanent ? "User banned permanently" : `User banned for ${duration_days} days`);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to ban user");
    }
  };

  const handleUnbanUser = async (userId) => {
    try {
      await apiClient.post(`/admin/users/${userId}/unban`);
      setUsers(users.map(u => u.id === userId ? { ...u, banned_permanently: false, banned_until: null } : u));
      toast.success("User unbanned");
    } catch (error) {
      toast.error("Failed to unban user");
    }
  };

  const handleResetUserPassword = async (userId, fullName) => {
    const newPassword = prompt(`Enter a new password for ${fullName} (min 6 chars):`);
    if (!newPassword) return;
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    try {
      await apiClient.post("/admin/users/reset-password", {
        user_id: userId,
        new_password: newPassword,
      });
      toast.success("Password reset successfully");
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to reset password");
    }
  };

  const handleCreateInvite = async () => {
    if (!INVITE_EXPIRY_OPTIONS.some((option) => option.value === Number(inviteDays))) {
      toast.error("Please select a valid invite expiry option");
      return;
    }

    setCreatingInvite(true);
    try {
      const response = await apiClient.post("/invites", {
        expires_in_days: Number(inviteDays),
      });
      setInvites([response.data, ...invites]);
      try {
        await navigator.clipboard.writeText(response.data.invite_url);
        toast.success("Invite created and link copied");
      } catch {
        toast.success("Invite created");
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to create invite");
    } finally {
      setCreatingInvite(false);
    }
  };

  const handleDeactivateInvite = async (inviteId) => {
    try {
      await apiClient.put(`/invites/${inviteId}/deactivate`);
      setInvites(invites.map((invite) =>
        invite.id === inviteId ? { ...invite, active: false } : invite
      ));
      toast.success("Invite deactivated");
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to deactivate invite");
    }
  };

  const handleCopyInviteLink = async (inviteUrl) => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      toast.success("Invite link copied");
    } catch {
      toast.error("Could not copy invite link");
    }
  };

  const handleProcessWithdrawal = async (withdrawalId, status, notes = null) => {
    try {
      await apiClient.put(`/admin/withdrawals/${withdrawalId}?status=${status}${notes ? `&notes=${encodeURIComponent(notes)}` : ''}`);
      setWithdrawals(withdrawals.map(w => w.id === withdrawalId ? { ...w, status } : w));
      toast.success(`Withdrawal ${status}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to process withdrawal");
    }
  };

  const handleAdjustBonusPoints = async () => {
    if (!selectedPointsUserId) {
      toast.error("Select a student first");
      return;
    }
    const normalizedPoints = Number(pointsValue);
    if (!Number.isInteger(normalizedPoints) || normalizedPoints < 0) {
      toast.error("Points must be a whole number (0 or more)");
      return;
    }
    if (!pointsReason || pointsReason.trim().length < 3) {
      toast.error("Please add a short reason (minimum 3 characters)");
      return;
    }

    setAdjustingPoints(true);
    try {
      let endpoint, after;
      if (pointsTarget === "mkcl") {
        // MKCL/ERA marks use a dedicated endpoint (PUT, set exact value 0-50)
        const response = await apiClient.put(`/admin/users/${selectedPointsUserId}/mkcl-era-marks`, {
          marks: normalizedPoints,
          reason: pointsReason.trim(),
        });
        after = response.data?.mkcl_era_marks ?? normalizedPoints;
        setUsers((prev) => prev.map((u) => (
          u.id === selectedPointsUserId ? { ...u, mkcl_era_marks: after } : u
        )));
        setAdminLeaderboard((prev) => prev.map((u) => (
          u.user_id === selectedPointsUserId ? { ...u, mkcl_era_marks: after } : u
        )));
        toast.success("MKCL/ERA marks updated");
      } else {
        endpoint = pointsTarget === "referral" ? "referral-points" : "bonus-points";
        const response = await apiClient.put(`/admin/users/${selectedPointsUserId}/${endpoint}`, {
          operation: pointsOperation,
          points: normalizedPoints,
          reason: pointsReason.trim(),
        });
        after = response.data?.after ?? 0;
        setUsers((prev) => prev.map((u) => (
          u.id === selectedPointsUserId
            ? { ...u, [pointsTarget === "referral" ? "referral_points" : "bonus_points"]: after }
            : u
        )));
        setAdminLeaderboard((prev) => prev.map((u) => (
          u.user_id === selectedPointsUserId
            ? { ...u, [pointsTarget === "referral" ? "referral_points" : "bonus_marks"]: after }
            : u
        )));
        toast.success(pointsTarget === "referral" ? "Referral points updated" : "Bonus marks updated");
      }
      setPointsValue(0);
      setPointsReason("");
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to update marks");
    } finally {
      setAdjustingPoints(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const eligibleUsers = users.filter((u) => ["student", "teacher"].includes(u.role));

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex flex-wrap items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-lg hover:bg-surface transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <ShieldCheck weight="fill" className="w-5 h-5 text-primary" />
            <h1 className="font-heading font-semibold">Admin Panel</h1>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: "stats", label: "Overview", icon: ShieldCheck },
            { id: "users", label: "Users", icon: Users },
            { id: "posts", label: "Posts", icon: Article },
            { id: "withdrawals", label: "Withdrawals", icon: Coins },
            ...( ["admin", "super_admin"].includes(user?.role)
              ? [
                  { id: "invites", label: "Invites", icon: LinkSimple },
                  { id: "points", label: "Leaderboard Points", icon: Trophy },
                ]
              : []),
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? "bg-primary text-primary-foreground"
                  : "bg-surface text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`admin-tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Stats Tab */}
        {activeTab === "stats" && stats && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-surface rounded-xl p-6 border border-border/40">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                    <Users className="w-5 h-5 text-primary" />
                  </div>
                </div>
                <div className="font-heading text-3xl font-bold">{stats.total_users}</div>
                <div className="text-sm text-muted-foreground">Total Users</div>
              </div>
              <div className="bg-surface rounded-xl p-6 border border-border/40">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center">
                    <Article className="w-5 h-5 text-accent" />
                  </div>
                </div>
                <div className="font-heading text-3xl font-bold">{stats.total_posts}</div>
                <div className="text-sm text-muted-foreground">Total Posts</div>
              </div>
              <div className="bg-surface rounded-xl p-6 border border-border/40">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-danger/20 flex items-center justify-center">
                    <ChatCircle className="w-5 h-5 text-danger" />
                  </div>
                </div>
                <div className="font-heading text-3xl font-bold">{stats.total_comments}</div>
                <div className="text-sm text-muted-foreground">Total Comments</div>
              </div>
              <div className="bg-surface rounded-xl p-6 border border-border/40">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-warning/20 flex items-center justify-center">
                    <GraduationCap className="w-5 h-5 text-warning" />
                  </div>
                </div>
                <div className="font-heading text-3xl font-bold">{stats.students}</div>
                <div className="text-sm text-muted-foreground">Students</div>
              </div>
            </div>

            {/* Referral Stats */}
            {referralStats && (
              <div className="bg-surface rounded-xl p-6 border border-border/40">
                <h3 className="font-heading font-semibold mb-4 flex items-center gap-2">
                  <Gift className="w-5 h-5 text-primary" />
                  Referral Program Stats
                </h3>
                <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
                  <div className="text-center p-3 bg-background rounded-lg">
                    <div className="font-heading text-2xl font-bold text-primary">{referralStats.total_referrals}</div>
                    <div className="text-xs text-muted-foreground">Total Referrals</div>
                  </div>
                  <div className="text-center p-3 bg-background rounded-lg">
                    <div className="font-heading text-2xl font-bold text-accent">{referralStats.total_points_awarded}</div>
                    <div className="text-xs text-muted-foreground">Points Awarded</div>
                  </div>
                  <div className="text-center p-3 bg-background rounded-lg">
                    <div className="font-heading text-2xl font-bold text-primary">{referralStats.total_bonus_points || 0}</div>
                    <div className="text-xs text-muted-foreground">Bonus Points</div>
                  </div>
                  <div className="text-center p-3 bg-background rounded-lg">
                    <div className="font-heading text-2xl font-bold text-warning">{referralStats.pending_withdrawals}</div>
                    <div className="text-xs text-muted-foreground">Pending Withdrawals</div>
                  </div>
                  <div className="text-center p-3 bg-background rounded-lg">
                    <div className="font-heading text-2xl font-bold">{referralStats.completed_withdrawals}</div>
                    <div className="text-xs text-muted-foreground">Completed</div>
                  </div>
                  <div className="text-center p-3 bg-background rounded-lg">
                    <div className="font-heading text-2xl font-bold text-danger">{referralStats.total_points_withdrawn}</div>
                    <div className="text-xs text-muted-foreground">Points Redeemed</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === "users" && (
          <div className="bg-surface rounded-xl border border-border/40 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border/40">
                    <th className="text-left p-4 font-medium text-muted-foreground">User</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Email</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Role</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Joined</th>
                    <th className="text-right p-4 font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id} className="border-b border-border/40 last:border-0" data-testid={`user-row-${u.id}`}>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary/70 to-accent/70 flex items-center justify-center text-sm font-bold text-background">
                            {u.full_name?.charAt(0).toUpperCase()}
                          </div>
                          <span className="font-medium">{u.full_name}</span>
                        </div>
                      </td>
                      <td className="p-4 text-muted-foreground">{u.email}</td>
                      <td className="p-4">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="flex items-center gap-1 px-3 py-1 rounded-full bg-surface-elevated text-sm capitalize">
                              {u.role === "student" ? <GraduationCap className="w-4 h-4" /> : <Chalkboard className="w-4 h-4" />}
                              {u.role}
                              <CaretDown className="w-3 h-3" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            <DropdownMenuItem onClick={() => handleRoleChange(u.id, "student")}>
                              <GraduationCap className="w-4 h-4 mr-2" />
                              Student
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleRoleChange(u.id, "teacher")}>
                              <Chalkboard className="w-4 h-4 mr-2" />
                              Teacher
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleRoleChange(u.id, "admin")}>
                              <ShieldCheck className="w-4 h-4 mr-2" />
                              Admin
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                      <td className="p-4 text-muted-foreground">{formatDate(u.created_at)}</td>
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleResetUserPassword(u.id, u.full_name)}
                            className="p-2 rounded-lg text-primary hover:bg-primary/10 transition-colors"
                            data-testid={`reset-password-user-${u.id}`}
                            title="Reset user password"
                          >
                            <Key className="w-4 h-4" />
                          </button>
                          {u.id !== user.id && ["student", "teacher"].includes(u.role) && (
                            <>
                              {u.banned_permanently || u.banned_until ? (
                                <button
                                  onClick={() => handleUnbanUser(u.id)}
                                  className="px-2 py-1 rounded-lg text-xs bg-accent/20 text-accent hover:bg-accent/30 transition-colors"
                                  data-testid={`unban-user-${u.id}`}
                                  title="Remove ban"
                                >
                                  Unban
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleBanUser(u.id, u.full_name)}
                                  className="px-2 py-1 rounded-lg text-xs bg-danger/10 text-danger hover:bg-danger/20 transition-colors"
                                  data-testid={`ban-user-${u.id}`}
                                  title="Ban student"
                                >
                                  Ban
                                </button>
                              )}
                              <button
                                onClick={() => handleDeleteUser(u.id)}
                                className="p-2 rounded-lg text-danger hover:bg-danger/10 transition-colors"
                                data-testid={`delete-user-${u.id}`}
                              >
                                <Trash className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Posts Tab */}
        {activeTab === "posts" && (
          <div className="bg-surface rounded-xl border border-border/40 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border/40">
                    <th className="text-left p-4 font-medium text-muted-foreground">Title</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Author</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Date</th>
                    <th className="text-right p-4 font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {posts.map(post => (
                    <tr key={post.id} className="border-b border-border/40 last:border-0" data-testid={`post-row-${post.id}`}>
                      <td className="p-4">
                        <span className="font-medium line-clamp-1">{post.title}</span>
                      </td>
                      <td className="p-4 text-muted-foreground">{post.author_name}</td>
                      <td className="p-4 text-muted-foreground">{formatDate(post.created_at)}</td>
                      <td className="p-4">
                        <div className="flex items-center gap-2 flex-wrap justify-end">
                          <button
                            onClick={() => handleFeaturePost(post, !post.is_featured)}
                            className={`px-2 py-1 rounded-lg text-xs transition-colors ${
                              post.is_featured ? "bg-primary/20 text-primary" : "text-muted-foreground hover:bg-surface-elevated"
                            }`}
                            data-testid={`feature-post-${post.id}`}
                            title={post.is_featured ? "Unfeature" : "Feature"}
                          >
                            {post.is_featured ? "★ Featured" : "☆ Feature"}
                          </button>
                          <button
                            onClick={() => handlePinPost(post, !post.is_pinned)}
                            className={`px-2 py-1 rounded-lg text-xs transition-colors ${
                              post.is_pinned ? "bg-warning/20 text-warning" : "text-muted-foreground hover:bg-surface-elevated"
                            }`}
                            data-testid={`pin-post-${post.id}`}
                            title={post.is_pinned ? "Unpin" : "Pin to top"}
                          >
                            Pin
                          </button>
                          <button
                            onClick={() => handleHidePost(post, post.status !== "hidden")}
                            className={`px-2 py-1 rounded-lg text-xs transition-colors ${
                              post.status === "hidden" ? "bg-muted text-muted-foreground" : "text-warning hover:bg-warning/10"
                            }`}
                            data-testid={`hide-post-${post.id}`}
                            title={post.status === "hidden" ? "Unhide" : "Hide from public wall"}
                          >
                            {post.status === "hidden" ? "Unhide" : "Hide"}
                          </button>
                          <button
                            onClick={() => handleDeletePost(post.id)}
                            className="p-2 rounded-lg text-danger hover:bg-danger/10 transition-colors"
                            data-testid={`delete-post-${post.id}`}
                          >
                            <Trash className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Withdrawals Tab */}
        {activeTab === "withdrawals" && (
          <div className="bg-surface rounded-xl border border-border/40 overflow-hidden">
            <div className="p-4 border-b border-border/40">
              <h3 className="font-heading font-semibold">Withdrawal Requests</h3>
              <p className="text-sm text-muted-foreground">Manage student reward redemption requests</p>
            </div>
            {withdrawals.length === 0 ? (
              <div className="p-8 text-center">
                <Coins className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
                <p className="text-muted-foreground">No withdrawal requests yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border/40">
                      <th className="text-left p-4 font-medium text-muted-foreground">User</th>
                      <th className="text-left p-4 font-medium text-muted-foreground">Points</th>
                      <th className="text-left p-4 font-medium text-muted-foreground">Date</th>
                      <th className="text-left p-4 font-medium text-muted-foreground">Status</th>
                      <th className="text-right p-4 font-medium text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {withdrawals.map(w => (
                      <tr key={w.id} className="border-b border-border/40 last:border-0" data-testid={`withdrawal-row-${w.id}`}>
                        <td className="p-4">
                          <div>
                            <p className="font-medium">{w.user_name}</p>
                            <p className="text-xs text-muted-foreground">{w.user_email}</p>
                          </div>
                        </td>
                        <td className="p-4">
                          <span className="font-mono font-bold text-primary">{w.points}</span>
                        </td>
                        <td className="p-4 text-muted-foreground">{formatDate(w.created_at)}</td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded-full text-xs capitalize ${
                            w.status === 'pending' ? 'bg-warning/20 text-warning' :
                            w.status === 'approved' || w.status === 'completed' ? 'bg-accent/20 text-accent' :
                            'bg-danger/20 text-danger'
                          }`}>
                            {w.status}
                          </span>
                        </td>
                        <td className="p-4 text-right">
                          {w.status === 'pending' && (
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => handleProcessWithdrawal(w.id, 'completed')}
                                className="p-2 rounded-lg text-accent hover:bg-accent/10 transition-colors"
                                title="Approve"
                                data-testid={`approve-withdrawal-${w.id}`}
                              >
                                <CheckCircle weight="fill" className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => {
                                  const notes = prompt("Rejection reason (optional):");
                                  handleProcessWithdrawal(w.id, 'rejected', notes);
                                }}
                                className="p-2 rounded-lg text-danger hover:bg-danger/10 transition-colors"
                                title="Reject"
                                data-testid={`reject-withdrawal-${w.id}`}
                              >
                                <XCircle weight="fill" className="w-5 h-5" />
                              </button>
                            </div>
                          )}
                          {w.status !== 'pending' && (
                            <span className="text-xs text-muted-foreground">
                              {w.processed_at && `Processed ${formatDate(w.processed_at)}`}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === "invites" && ["admin", "super_admin"].includes(user?.role) && (
          <div className="space-y-4">
            <div className="bg-surface rounded-xl border border-border/40 p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <h3 className="font-heading font-semibold">Invite Management</h3>
                  <p className="text-sm text-muted-foreground">Create and manage single-use admission invites</p>
                </div>
                <div className="flex items-center gap-2">
                  <div>
                    <label className="text-xs text-muted-foreground">Expires in</label>
                    <select
                      value={inviteDays}
                      onChange={(e) => setInviteDays(Number(e.target.value))}
                      className="w-28 h-10 rounded-lg bg-background border border-border px-3"
                      data-testid="invite-expiry-days-input"
                    >
                      {INVITE_EXPIRY_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <Button
                    onClick={handleCreateInvite}
                    disabled={creatingInvite}
                    data-testid="create-invite-button"
                  >
                    {creatingInvite ? "Creating..." : "Create Invite"}
                  </Button>
                </div>
              </div>
            </div>

            <div className="bg-surface rounded-xl border border-border/40 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border/40">
                      <th className="text-left p-4 font-medium text-muted-foreground">Code</th>
                      <th className="text-left p-4 font-medium text-muted-foreground">Status</th>
                      <th className="text-left p-4 font-medium text-muted-foreground">Expires</th>
                      <th className="text-left p-4 font-medium text-muted-foreground">Used By</th>
                      <th className="text-right p-4 font-medium text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invites.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="p-8 text-center text-muted-foreground">
                          No invites created yet
                        </td>
                      </tr>
                    ) : (
                      invites.map((invite) => (
                        <tr key={invite.id} className="border-b border-border/40 last:border-0" data-testid={`invite-row-${invite.id}`}>
                          <td className="p-4">
                            <span className="font-mono font-semibold">{invite.code}</span>
                          </td>
                          <td className="p-4">
                            <span className={`px-2 py-1 rounded-full text-xs capitalize ${
                              invite.active && !invite.used_at
                                ? "bg-accent/20 text-accent"
                                : "bg-muted text-muted-foreground"
                            }`}>
                              {invite.used_at ? "used" : invite.active ? "active" : "inactive"}
                            </span>
                          </td>
                          <td className="p-4 text-muted-foreground">{formatDate(invite.expires_at)}</td>
                          <td className="p-4 text-muted-foreground">{invite.used_by_user_id || "-"}</td>
                          <td className="p-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              {invite.invite_url && (
                                <button
                                  onClick={() => handleCopyInviteLink(invite.invite_url)}
                                  className="p-2 rounded-lg text-primary hover:bg-primary/10 transition-colors"
                                  title="Copy invite link"
                                  data-testid={`copy-invite-${invite.id}`}
                                >
                                  <LinkSimple className="w-4 h-4" />
                                </button>
                              )}
                              {invite.active && !invite.used_at && (
                                <button
                                  onClick={() => handleDeactivateInvite(invite.id)}
                                  className="p-2 rounded-lg text-danger hover:bg-danger/10 transition-colors"
                                  title="Deactivate invite"
                                  data-testid={`deactivate-invite-${invite.id}`}
                                >
                                  <XCircle className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === "points" && ["admin", "super_admin"].includes(user?.role) && (
          <div className="space-y-4">
            <div className="bg-surface rounded-xl border border-border/40 p-4">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div>
                  <h3 className="font-heading font-semibold">Manage Student Points</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Add, remove, or set referral balance and leaderboard bonus points for students and teachers.
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  className="shrink-0 gap-2"
                  onClick={handleExportLeaderboard}
                >
                  <DownloadSimple className="w-4 h-4" />
                  Export Leaderboard
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3 mt-6">
                <div className="lg:col-span-2">
                  <label className="text-xs text-muted-foreground">Student/Teacher</label>
                  <select
                    value={selectedPointsUserId}
                    onChange={(e) => setSelectedPointsUserId(e.target.value)}
                    className="w-full h-10 rounded-lg bg-background border border-border px-3"
                    data-testid="bonus-points-user-select"
                  >
                    <option value="">Select user</option>
                    {eligibleUsers.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.full_name} ({u.role})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Points Type</label>
                  <select
                    value={pointsTarget}
                    onChange={(e) => setPointsTarget(e.target.value)}
                    className="w-full h-10 rounded-lg bg-background border border-border px-3"
                    data-testid="bonus-points-target-select"
                  >
                    <option value="bonus">🔵 Bonus Marks (Light Blue)</option>
                    <option value="mkcl">🟠 MKCL/ERA Marks (0–50, set exact)</option>
                    <option value="referral">🟢 Referral Balance</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Operation</label>
                  <select
                    value={pointsOperation}
                    onChange={(e) => setPointsOperation(e.target.value)}
                    className="w-full h-10 rounded-lg bg-background border border-border px-3"
                    data-testid="bonus-points-operation-select"
                  >
                    <option value="add">Add</option>
                    <option value="remove">Remove</option>
                    <option value="set">Set Exact</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Points</label>
                  <input
                    type="number"
                    min={0}
                    step={1}
                    value={pointsValue}
                    onChange={(e) => setPointsValue(e.target.value)}
                    className="w-full h-10 rounded-lg bg-background border border-border px-3"
                    data-testid="bonus-points-value-input"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Reason</label>
                  <input
                    type="text"
                    value={pointsReason}
                    onChange={(e) => setPointsReason(e.target.value)}
                    placeholder="Performance bonus"
                    className="w-full h-10 rounded-lg bg-background border border-border px-3"
                    data-testid="bonus-points-reason-input"
                  />
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <Button
                  onClick={handleAdjustBonusPoints}
                  disabled={adjustingPoints}
                  data-testid="bonus-points-submit"
                >
                  {adjustingPoints ? "Updating..." : "Update Points"}
                </Button>
              </div>
            </div>

            <div className="bg-surface rounded-xl border border-border/40 overflow-hidden">
              <div className="p-4 border-b border-border/40">
                <h4 className="font-heading font-semibold">Current Bonus Points</h4>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border/40">
                      <th className="text-left p-4 font-medium text-muted-foreground">User</th>
                      <th className="text-left p-4 font-medium text-muted-foreground">Role</th>
                      <th className="text-right p-4 font-medium text-muted-foreground">Referral Pts</th>
                      <th className="text-right p-4 font-medium text-muted-foreground" style={{color:"#38bdf8"}}>Bonus Marks</th>
                      <th className="text-right p-4 font-medium text-muted-foreground" style={{color:"#f97316"}}>MKCL/ERA</th>
                      <th className="text-right p-4 font-medium text-muted-foreground" style={{color:"#c084fc"}}>Engage Pts</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminLeaderboard.map((u) => {
                      const scoreBreakdown = u.score_breakdown || {};
                      return (
                        <tr key={u.user_id} className="border-b border-border/40 last:border-0">
                          <td className="p-4">{u.full_name}</td>
                          <td className="p-4 capitalize text-muted-foreground">{u.role}</td>
                          <td className="p-4 text-right font-mono font-semibold text-accent">{scoreBreakdown.referral_marks ?? u.referral_marks ?? u.referral_points ?? 0}</td>
                          <td className="p-4 text-right font-mono font-semibold" style={{color:"#38bdf8"}}>{scoreBreakdown.bonus_marks ?? u.bonus_marks ?? 0}</td>
                          <td className="p-4 text-right font-mono font-semibold" style={{color:"#f97316"}}>{scoreBreakdown.mkcl_era_marks ?? u.mkcl_era_marks ?? 0}</td>
                          <td className="p-4 text-right font-mono font-semibold" style={{color:"#c084fc"}}>{Math.round(scoreBreakdown.engagement_marks ?? u.engagement_marks ?? 0)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
